// P4 AI Tutor -- fetch()+ReadableStream SSE client.
//
// WP-03 (doc 10 آ§13, doc 12) corrects the connection target: the browser now
// opens a same-origin Subway SSE proxy route
// (`/api/ai-tutor/courses/:courseName/sessions/:sessionId/events`) with
// ordinary cookie-session credentials, never a custom `Authorization`
// header, `x-course-id`, or `x-client-id` -- Subway derives all of that
// server-side and proxies the upstream Synapse stream through. `EventSource`
// is still not used: even though no custom header is needed anymore,
// `EventSource` auto-reconnects on its own, which would silently reintroduce
// the reconnect/backoff behavior WP-03 explicitly does not implement yet;
// manual `fetch`+`ReadableStream` keeps that decision explicit and under this
// module's own control.
//
// Otherwise mirrors the proven Mock behavior exactly -- it is the only real
// evidence of what actually works against the upstream Synapse contract:
// manual `\n\n`-delimited SSE frame parsing (malformed frames dropped, not
// thrown), an AbortController per subscription, `done` (not stream EOF) as
// the completion signal, and NO reconnect/backoff logic in this module.
//
// BUG-22 (AI Tutor SSE reliability): the stream's TRANSPORT ending (open
// failure, HTTP error, read error, EOF, or Subway's own in-stream failure
// notice) is reported through `onDisconnect`, never through `onError`, which
// stays reserved for the Tutor's own `error` event. A transport loss says
// nothing about any reply -- the store decides what it means (reconnect,
// and recovery only if a reply was being generated). Subway's heartbeat
// comment frames (`:`) carry no `data:` line and are skipped by `parseFrame`.

import config from '@/config'

/**
 * @param {string} frame
 * @returns {import('./dto').SessionEvent|null}
 */
function parseFrame(frame) {
  const dataLines = frame
    .split('\n')
    .filter(line => line.startsWith('data:'))
    .map(line => line.slice('data:'.length).trim())

  if (dataLines.length === 0) return null

  try {
    return JSON.parse(dataLines.join('\n'))
  } catch {
    // Malformed frames are silently dropped, matching Track B آ§9's proven
    // Mock behavior exactly.
    return null
  }
}

/**
 * Subway's in-stream failure notice (the existing `error` event shape plus
 * one of Subway's stable string codes, e.g. `AI_TUTOR_UNAVAILABLE`) -- the
 * stream transport failing, not the Tutor failing a reply. The Tutor's own
 * `error` events carry no string `code`.
 * @param {Object} event
 */
function isTransportNotice(event) {
  return event.type === 'error' && typeof event.code === 'string'
}

/**
 * AT-API-020-H1 (API 0.2.0, migration reference §5): the stream carries
 * `state`, `text`, `done` and `error`, and nothing else. There is no `ask`
 * event any more (a turn never pauses); it, like any other unknown type, is
 * dropped here and reaches no handler.
 * @param {import('./dto').SessionEvent} event
 * @param {TutorEventHandlers} handlers
 */
function dispatch(event, handlers) {
  switch (event.type) {
    case 'state':
      handlers.onState?.(event)
      break
    case 'text':
      handlers.onText?.(event)
      break
    case 'done':
      handlers.onDone?.(event)
      break
    case 'error':
      handlers.onError?.(event.message)
      break
    default:
      break
  }
}

/**
 * @typedef {Object} TutorEventHandlers
 * @property {(event: import('./dto').SessionEvent) => void} [onState]
 * @property {(event: import('./dto').SessionEvent) => void} [onText]
 * @property {(event: import('./dto').SessionEvent) => void} [onDone]
 * @property {(message: string) => void} [onError] the Tutor's own `error`
 *   event
 * @property {() => void} [onOpen] the stream's response arrived (200 SSE)
 * @property {(loss: StreamLoss) => void} [onDisconnect] the transport ended
 *   for any reason other than `stop()` -- called at most once
 */

/**
 * @typedef {Object} StreamLoss
 * @property {'network'|'http'|'interrupted'|'closed'|'unavailable'} reason
 *   network: the request never got a response; http: a non-2xx response;
 *   interrupted: reading the body failed; closed: the body ended;
 *   unavailable: Subway reported the upstream stream failing
 * @property {number} [status] the HTTP status, for `http`
 * @property {string} [code] Subway's stable code, for `unavailable`
 */

/**
 * Opens an SSE subscription for one session's events, proxied through
 * Subway's same-origin route
 * (`GET /api/ai-tutor/courses/:courseName/sessions/:sessionId/events`,
 * doc 10 آ§13). The caller owns the lifecycle: call `stop()` before opening a
 * new subscription when the active session id changes, and on unmount --
 * exactly matching the proven Mock behavior. No reconnect/backoff is
 * attempted here; `onDisconnect` tells the caller the transport is gone.
 * @param {string} courseName -- education_courses.name, the frozen selected
 *   course from `activeSetupContext.subject`
 * @param {string} sessionId
 * @param {TutorEventHandlers} [handlers]
 * @returns {{ stop: () => void }}
 */
export function openTutorEventStream(courseName, sessionId, handlers = {}) {
  const controller = new AbortController()
  let ended = false

  // Aborted fetches (`stop()`, navigation away) are never surfaced (Track B
  // آ§9), and a transport loss is reported once.
  const disconnect = loss => {
    if (ended || controller.signal.aborted) return
    ended = true
    handlers.onDisconnect?.(loss)
  }

  ;(async () => {
    let response
    try {
      response = await fetch(
        `${config.serverRoot}/api/ai-tutor/courses/${encodeURIComponent(
          courseName
        )}/sessions/${encodeURIComponent(sessionId)}/events`,
        {
          // Same-origin cookie-session auth -- no Authorization header, no
          // x-course-id/x-client-id, no AI Tutor credential of any kind
          // (doc 10 آ§4/آ§9).
          credentials: 'include',
          headers: { Accept: 'text/event-stream' },
          signal: controller.signal,
        }
      )
    } catch {
      disconnect({ reason: 'network' })
      return
    }

    if (!response.ok || !response.body) {
      disconnect({ reason: 'http', status: response.status })
      return
    }
    if (controller.signal.aborted) return
    handlers.onOpen?.()

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        const frames = buffer.split('\n\n')
        buffer = frames.pop()
        for (const frame of frames) {
          if (ended || controller.signal.aborted) return
          const event = parseFrame(frame)
          if (!event) continue
          if (isTransportNotice(event)) {
            disconnect({ reason: 'unavailable', code: event.code })
            // Subway ends the response right after its notice; nothing
            // after it is read.
            reader.cancel().catch(() => {})
            return
          }
          dispatch(event, handlers)
        }
      }
    } catch {
      disconnect({ reason: 'interrupted' })
      return
    }
    disconnect({ reason: 'closed' })
  })()

  return {
    stop: () => controller.abort(),
  }
}
